import { useIonActionSheet } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useLongPress } from 'ahooks'
import { useRef } from 'react'
import {
  type NavAddress,
  defaultNavigationApp,
  getNavApp,
  navigators,
  openNavigation,
  setNavApp,
} from '../helpers/navigate'

/**
 * Navigation interaction, ported from v1's `useNavigateInteractionHandler`:
 * `navigate(addresses)` opens the user's saved map app, or — when none is saved
 * — presents an action sheet to pick one (and remembers the choice). The saved
 * app is the same `crew-nav-app` preference set in Profile.
 * `choose(addresses, { skipSave })` re-opens the picker (highlighting the
 * current app) without overwriting the saved preference.
 */
export const useNavigate = () => {
  const { _ } = useLingui()
  const [present] = useIonActionSheet()

  const choose = (
    addresses: NavAddress[],
    { skipSave = false }: { skipSave?: boolean } = {},
  ) => {
    if (!addresses.length) return
    const current = getNavApp() ?? defaultNavigationApp()
    present({
      header: _(msg`Select Navigation App`),
      buttons: [
        ...navigators().map(([app, reg]) => ({
          text: reg.title,
          cssClass: app === current ? 'action-sheet-default' : undefined,
          handler: () => {
            if (!skipSave) setNavApp(app)
            openNavigation(addresses, app)
          },
        })),
        { text: _(msg`Cancel`), role: 'cancel' as const },
      ],
    })
  }

  const navigate = (addresses: NavAddress[]) => {
    if (!addresses.length) return
    const saved = getNavApp()
    if (saved) openNavigation(addresses, saved)
    else choose(addresses)
  }

  return { navigate, choose }
}

/**
 * Press handling for a navigation trigger (v1 parity): tap navigates with the
 * saved app; long-press re-opens the picker to use another app one time.
 * Attach the returned ref to the button/link element.
 */
export const useNavigatePress = <T extends HTMLElement = HTMLElement>(
  addresses: NavAddress[],
) => {
  const ref = useRef<T>(null)
  const { navigate, choose } = useNavigate()

  useLongPress(() => choose(addresses, { skipSave: true }), ref, {
    onClick: () => navigate(addresses),
  })

  return ref
}
